import { useState } from 'react';
import { Outlet, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { DashboardLayout } from '../dashboard/components/DashboardLayout';
import { DashboardNav } from '../dashboard/components/DashboardNav';
import { DashboardHeader } from '../dashboard/components/DashboardHeader';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { LogoutIcon } from '../../assets/LogoutIcon';
import { useItineraryRequestCount } from '../../hooks/useItineraryRequestCount';
import { formatBadgeCount } from '../dashboard/sections/duplicates/duplicateSelection';
import './AdminLayout.css';

/**
 * Get the header title based on the current admin route
 */
const getAdminTitle = (pathname: string): string => {
  if (pathname.includes('/admin/organizations/')) {
    return 'Organization Details';
  }
  if (pathname.startsWith('/admin/roster-uploads/')) {
    return 'Roster Upload';
  }
  if (pathname.startsWith('/admin/roster-uploads')) {
    return 'Uploaded Rosters';
  }
  if (pathname.includes('/admin/batch-extractions')) {
    if (pathname.includes('/review')) {
      return 'Review Extraction';
    }
    if (pathname.includes('/new') || pathname.includes('/processing')) {
      return 'New Batch Extraction';
    }
    return 'Batch Extractions';
  }
  if (pathname.includes('/admin/notifications')) {
    return 'Notifications';
  }
  if (pathname.includes('/admin/scraping')) {
    return 'Web Scraping';
  }
  if (pathname.includes('/admin/messaging-numbers')) {
    return 'Messaging Numbers';
  }
  if (pathname.includes('/admin/itinerary-requests')) {
    return 'Itinerary Requests';
  }
  if (pathname.includes('/admin/sample-data')) {
    return 'Sample Data';
  }
  if (pathname.includes('/admin/billing/settings')) {
    return 'Billing Settings';
  }
  if (pathname.includes('/admin/billing/organizations/')) {
    return 'Organization Billing';
  }
  if (pathname.includes('/admin/billing')) {
    return 'Billing';
  }
  if (pathname.includes('/admin/tours/')) {
    return 'Edit Tour';
  }
  if (pathname.includes('/admin/tours')) {
    return 'Tours';
  }
  return 'System Administration';
};

/**
 * AdminLayout wraps all system admin routes.
 * It provides the dashboard shell (nav, header) and renders child routes via Outlet.
 */
export const AdminLayout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Every route under here is system_admin, so the count endpoint is always callable. The same
  // number is on the Admin item in the left-hand nav; carrying it through to the sub-item is what
  // turns "something is waiting" into a destination.
  const { count: pendingItineraryRequests } = useItineraryRequestCount();

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  return (
    <DashboardLayout>
      <DashboardNav
        onLogout={handleLogoutClick}
      />
      <div className="dashboard-content">
        <DashboardHeader
          title={getAdminTitle(location.pathname)}
          userEmail={user?.email || 'Unknown'}
        />
        <nav className="admin-subnav">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/organizations') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Organizations
          </NavLink>
          <NavLink
            to="/admin/roster-uploads"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/roster-uploads') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Uploaded Rosters
          </NavLink>
          <NavLink
            to="/admin/batch-extractions"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/batch-extractions') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Batch Extractions
          </NavLink>
          <NavLink
            to="/admin/notifications"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/notifications') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Notifications
          </NavLink>
          <NavLink
            to="/admin/scraping"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/scraping') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Web Scraping
          </NavLink>
          <NavLink
            to="/admin/itinerary-requests"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/itinerary-requests') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Itinerary Requests
            {pendingItineraryRequests > 0 && (
              <span
                className="admin-subnav__badge"
                aria-label={`${pendingItineraryRequests} itinerary requests waiting`}
              >
                {formatBadgeCount(pendingItineraryRequests)}
              </span>
            )}
          </NavLink>
          <NavLink
            to="/admin/messaging-numbers"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/messaging-numbers') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Messaging Numbers
          </NavLink>
          <NavLink
            to="/admin/billing"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/billing') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Billing
          </NavLink>
          <NavLink
            to="/admin/tours"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive || location.pathname.startsWith('/admin/tours') ? 'admin-subnav__link--active' : ''}`
            }
          >
            Tours
          </NavLink>
          <NavLink
            to="/admin/sample-data"
            className={({ isActive }) =>
              `admin-subnav__link ${isActive ? 'admin-subnav__link--active' : ''}`
            }
          >
            Sample data
          </NavLink>
        </nav>
        <main className="dashboard-section-container" id="main-content" tabIndex={-1}>
          <ErrorBoundary
            fallbackTitle="Admin Section Error"
            fallbackMessage="This section encountered an error. Try refreshing the page."
          >
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes, Logout"
        cancelText="Cancel"
        icon={<LogoutIcon />}
      />
    </DashboardLayout>
  );
};
